import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComplianceReviewer } from '@shared/api/contracts/compliance';
import type { ReasonFactorValue } from '@shared/ui';
import { LoadErrorBanner, ReasonFactorDialog, Skeleton, SkeletonStatus, StatusBanner, Switch, TypographyMuted } from '@shared/ui';

interface ReviewersPanelProps {
  /** undefined — список ещё ни разу не загружался (загрузка или ошибка). */
  items: ComplianceReviewer[] | undefined;
  listLoading?: boolean;
  listError?: unknown;
  listRetrying?: boolean;
  onRetry?: () => void;
  userId: number | null;
  loading?: boolean;
  onToggle: (userId: number, enabled: boolean, value: ReasonFactorValue) => Promise<boolean>;
}

export function ReviewersPanel({
  items,
  listLoading = false,
  listError,
  listRetrying = false,
  onRetry,
  userId,
  loading,
  onToggle,
}: ReviewersPanelProps) {
  const { t } = useTranslation('cabinet');
  const [target, setTarget] = useState<{ id: number; enabled: boolean } | null>(null);
  const hasError = Boolean(listError);
  const loaded = items !== undefined;
  const list = items ?? [];
  const reviewers = list.filter((item) => item.complianceReviewer).length;

  return (
    <div className="surface-card space-y-4">
      {hasError ? (
        <LoadErrorBanner error={listError} stale={loaded} retrying={listRetrying} onRetry={() => onRetry?.()} />
      ) : null}
      {!loaded && !hasError && listLoading ? (
        <SkeletonStatus className="space-y-3" label={t('workspace.loading')}>
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </SkeletonStatus>
      ) : null}
      {loaded && reviewers < 2 ? <StatusBanner>{t('compliance.reviewers.needTwo')}</StatusBanner> : null}
      {loaded && !list.length ? <TypographyMuted>{t('compliance.reviewers.empty')}</TypographyMuted> : null}
      <ul className="divide-y divide-border">
        {list.map((item) => {
          const self = userId !== null && item.id === userId;
          return (
            <li key={item.id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{item.email}</p>
                {item.bannedAt ? <p className="text-xs text-muted-foreground">{t('compliance.reviewers.banned')}</p> : null}
                {self ? <p className="text-xs text-muted-foreground">{t('compliance.reviewers.self')}</p> : null}
              </div>
              <Switch
                checked={item.complianceReviewer}
                disabled={self || Boolean(item.bannedAt) || loading}
                aria-label={item.email}
                onCheckedChange={(checked) => setTarget({ id: item.id, enabled: checked === true })}
              />
            </li>
          );
        })}
      </ul>
      <ReasonFactorDialog
        open={Boolean(target)}
        title={t(target?.enabled ? 'compliance.reviewers.grantTitle' : 'compliance.reviewers.revokeTitle')}
        description={t('compliance.reviewers.description')}
        confirmLabel={t('compliance.reviewers.save')}
        loading={loading}
        onOpenChange={(open) => {
          if (!open) setTarget(null);
        }}
        onConfirm={(value) => {
          if (!target) return;
          void onToggle(target.id, target.enabled, value).then((ok) => {
            if (ok) setTarget(null);
          });
        }}
      />
    </div>
  );
}
